
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, User, UserPlus, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface CreateUserDialogProps {
  onUserCreated: () => void;
}

export const CreateUserDialog = ({ onUserCreated }: CreateUserDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    firstName: "",
    lastName: "",
    role: "employee" as AppRole,
    password: ""
  });

  const createInvitation = async () => {
    try {
      setLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Ingen aktiv session fundet");
      }

      // Kald create_invitation med alle felterne
      const { data, error } = await supabase.rpc('create_invitation', {
        user_email: newUser.email,
        user_first_name: newUser.firstName,
        user_last_name: newUser.lastName,
        user_password: newUser.password,
        user_role: newUser.role,
        admin_id: session.user.id
      });

      if (error) throw error;

      // Send invitation email via Resend edge function
      const emailResponse = await supabase.functions.invoke('send-invitation', {
        body: {
          email: newUser.email,
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          invitationToken: data,
          password: newUser.password
        }
      });

      if (emailResponse.error) throw emailResponse.error;

      toast({
        title: "Invitation sendt",
        description: "En invitationsmail er blevet sendt til den nye bruger.",
      });

      setIsDialogOpen(false);
      onUserCreated();
    } catch (error: any) {
      console.error('Fejl ved oprettelse af invitation:', error);
      toast({
        title: "Fejl ved oprettelse af invitation",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="mr-2 h-4 w-4" />
          Inviter ny bruger
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Inviter ny bruger</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="email"
                type="email"
                className="pl-10 bg-white"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="firstName">Fornavn</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="firstName"
                className="pl-10 bg-white"
                value={newUser.firstName}
                onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Efternavn</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="lastName"
                className="pl-10 bg-white"
                value={newUser.lastName}
                onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Adgangskode</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="password"
                type="text"
                className="pl-10 bg-white"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">Rolle</Label>
            <Select
              value={newUser.role}
              onValueChange={(value: AppRole) => 
                setNewUser(prev => ({ ...prev, role: value }))
              }
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Vælg rolle" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="employee">Medarbejder</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={createInvitation}
            disabled={loading}
            className="w-full"
          >
            {loading ? "Sender invitation..." : "Send invitation"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
